
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: "Learn how to add Plaid's Investments Move to your application to streamline brokerage-to-brokerage account transfers."
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Investments Move to your app' subtitle='Use Investments Move to streamline brokerage-to-brokerage account transfers' mdxType="Header" />
    <p>{`In this guide, we'll start from scratch and walk through how to use Investments Move to get the data required to set up an ACATS transfer. If you are already familiar with using Plaid and are set up to make calls to the Plaid API, make sure to initialize Link with the `}<inlineCode parentName="p">{`investments_auth`}</inlineCode>{` product; you can then skip ahead to `}<a parentName="p" {...{
        "href": "#fetching-investments-move-data"
      }}>{`Fetching Investments Move data`}</a>{`.`}</p>
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-profile"
    }}>{`Get Plaid API keys and complete application profile`}</h4>
    <p>{`If you don't already have one, you'll need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You will also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` on the Dashboard. The information in your profile will be shared with users of your application when they manage their connection on the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`. Your application profile must be completed before connecting to certain institutions in Production.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include them in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "create-an-item-in-link"
    }}>{`Create an Item in Link`}</h4>
    <p>{`Plaid Link is a drop-in module that provides a secure, elegant authentication flow
for each institution that Plaid supports. Link makes it secure and easy for users to
connect their bank accounts to Plaid. Note that these instructions cover Link on the web. For instructions on using Link within mobile apps, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{`.`}</p>
    <p>{`Using Link, we will create a Plaid `}<em parentName="p">{`Item`}</em>{`, which is a Plaid term for a login at a financial institution. An Item is not the same as a financial institution account, although every account will be associated with an Item. For example, if a user has one login at their bank that allows them to access both their checking account and their savings account, a single Item would be associated with both of those accounts.`}</p>
    <p>{`First, on the client side of your application, you'll need to set up and configure Link. If you want to customize Link's look and feel, you can do so from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link"
      }}>{`Dashboard`}</a>{`.`}</p>
    <p>{`When initializing Link, you will need to specify the products you will be using in the product array.`}</p>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <SampleCode.CreateLinkToken product='investments_auth' />
    <p>{`When using Investments Move, you can also configure options in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call to allow more brokerage accounts to be added, with the tradeoff that Plaid may not be able to verify all of the information. For details, see `}<a parentName="p" {...{
        "href": "/docs/investments-move/#fallback-flows"
      }}>{`Fallback flows`}</a>{`.`}</p>
    <h5 {...{
      "id": "install-link-dependency"
    }}>{`Install Link dependency`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "#index.html",
        "#index.html": true
      }}>{`<head>
  <title>Connect a bank</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler"
    }}>{`Configure the client-side Link handler`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "switcher=false #app.js",
        "switcher": "false",
        "#app.js": true
      }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token to your app server.
    $.post('/exchange_public_token', {
      public_token: public_token,
    });
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
    <h4 {...{
      "id": "get-a-persistent-access_token"
    }}>{`Get a persistent access_token`}</h4>
    <p>{`Next, on the server side, we need to exchange our `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{`. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will allow us to make authenticated calls to the Plaid API. Doing so is as easy as calling the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` endpoint from our server-side handler. We'll use the client library we configured earlier to make the API call.`}</p>
    <p>{`Save the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` in a secure datastore, as they're used to access `}<inlineCode parentName="p">{`Item`}</inlineCode>{` data and identify `}<inlineCode parentName="p">{`webhooks`}</inlineCode>{`, respectively. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will remain valid unless you actively choose to expire it via rotation or remove the corresponding Item via `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{`. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` should be stored securely, and never in client-side code. A `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` is a one-time use token with a lifetime of 30 minutes, so there is no need to store it.`}</p>
    <SampleCode.ExchangePublicToken />
    <h4 {...{
      "id": "fetching-investments-move-data"
    }}>{`Fetching Investments Move data`}</h4>
    <p>{`Now that the authentication step is out of the way, we can begin using authenticated endpoints from the Plaid API. For more detailed information on the schema for account information returned, see `}<inlineCode parentName="p">{`/investments/auth/get`}</inlineCode>{`.`}</p>
    <MDXCodeBlock title='/investments/auth/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/investments/auth/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": String
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: InvestmentsAuthGetRequest = {
  access_token: accessToken,
};
try {
  const response = await plaidClient.investmentsAuthGet(request);
  const investmentsAuthData = response.data;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = InvestmentsAuthGetRequest(access_token=access_token)
response = client.investments_auth_get(request)
investmentsAuthData = response
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`InvestmentsAuthGetRequest request = new InvestmentsAuthGetRequest()
  .accessToken(accessToken);
Response<InvestmentsAuthGetResponse> response = client()
  .investmentsAuthGet(request)
  .execute();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::InvestmentsAuthGetRequest.new({ access_token: access_token })
response = client.investments_auth_get(request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewInvestmentsAuthGetRequest(accessToken)
resp, _, err := client.PlaidApi.InvestmentsAuthGet(ctx).InvestmentsAuthGetRequest(*request).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.InvestmentsAuthGetRequest{AccessToken: accessToken}
resp, err := client.Investments.GetAuth(ctx, request)
`}</code></pre>
    </MDXCodeBlock>
    <p>{`The results of the `}<inlineCode parentName="p">{`/investments/auth/get`}</inlineCode>{` call return the information you need to submit an ACATS transfer, using verified data derived directly from the brokerage. Example response data is below. For more details on the schema of data returned, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/investments-move/#investments-auth-get-response-accounts"
      }}>{`API Reference`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Investments move sample response",
        "#Investments": true,
        "move": true,
        "sample": true,
        "response": true
      }}>{`{
  "accounts": [
    {
      "account_id": "31qEA6LPwGumkA4Z5mGbfyGwr4mL6nSZlQqpZ",
      "balances": {
        "available": 43200,
        "current": 43200,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "4444",
      "name": "Plaid Money Market",
      "official_name": "Plaid Platinum Standard 1.85% Interest Money Market",
      "subtype": "money market",
      "type": "depository"
    },
    {
      "account_id": "xlP8npRxwgCj48LQbjxWipkeL3gbyXf64knoy",
      "balances": {
        "available": null,
        "current": 320.76,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "5555",
      "name": "Plaid IRA",
      "official_name": null,
      "subtype": "ira",
      "type": "investment"
    }
  ],
  "holdings": [
    {
      "account_id": "xlP8npRxwgCj48LQbjxWipkeL3gbyXf64knoy",
      "cost_basis": 1,
      "institution_price": 1,
      "institution_price_as_of": "2021-05-25",
      "institution_price_datetime": null,
      "institution_value": 0.01,
      "iso_currency_code": "USD",
      "quantity": 0.01,
      "security_id": "d6ePmbPxgWCWmMVv66q9iPV94n91vMtov5Are",
      "unofficial_currency_code": null,
      "vested_quantity": 0.01,
      "vested_value": 0.01,
      "tax_lots": []
    },
    {
      "account_id": "xlP8npRxwgCj48LQbjxWipkeL3gbyXf64knoy",
      "cost_basis": 0.01,
      "institution_price": 0.011,
      "institution_price_as_of": "2021-05-25",
      "institution_price_datetime": null,
      "institution_value": 110,
      "iso_currency_code": "USD",
      "quantity": 10000,
      "security_id": "8E4L9XLl6MudjEpwPAAgivmdZRdBPJuvMPlPb",
      "unofficial_currency_code": null,
      "vested_quantity": null,
      "vested_value": null,
      "tax_lots": []
    },
    {
      "account_id": "xlP8npRxwgCj48LQbjxWipkeL3gbyXf64knoy",
      "cost_basis": 40,
      "institution_price": 42.15,
      "institution_price_as_of": "2021-05-25",
      "institution_price_datetime": null,
      "institution_value": 210.75,
      "iso_currency_code": "USD",
      "quantity": 5,
      "security_id": "abJamDazkgfvBkVGgnnLUWXoxnomp5up8llg4",
      "unofficial_currency_code": null,
      "vested_quantity": 5,
      "vested_value": 210.75,
      "tax_lots": []
    }
  ],
  "item": {
    "available_products": [
      "assets",
      "balance",
      "cra_base_report",
      "cra_income_insights",
      "signal",
      "identity",
      "identity_match",
      "income",
      "income_verification",
      "investments",
      "processor_identity",
      "recurring_transactions",
      "transactions"
    ],
    "billed_products": [
      "investments_auth"
    ],
    "consent_expiration_time": null,
    "error": null,
    "institution_id": "ins_115616",
    "item_id": "7qBnDwLP3aIZkD7NKZ5ysk5X9xVxDWHg65oD5",
    "products": [
      "investments_auth"
    ],
    "update_type": "background",
    "webhook": "https://www.genericwebhookurl.com/webhook"
  },
  "numbers": {
    "acats": [
      {
        "account": "TR5555",
        "account_id": "xlP8npRxwgCj48LQbjxWipkeL3gbyXf64knoy",
        "dtc_numbers": [
          "1111",
          "2222",
          "3333"
        ]
      }
    ]
  },
  "owners": [
    {
      "account_id": "31qEA6LPwGumkA4Z5mGbfyGwr4mL6nSZlQqpZ",
      "names": [
        "Alberta Bobbeth Charleson"
      ]
    },
    {
      "account_id": "xlP8npRxwgCj48LQbjxWipkeL3gbyXf64knoy",
      "names": [
        "Alberta Bobbeth Charleson"
      ]
    }
  ],
  "request_id": "hPCXou4mm9Qwzzu",
  "securities": [
    {
      "cfi_code": "OCASPS",
      "close_price": 0.011,
      "close_price_as_of": null,
      "cusip": null,
      "figi": null,
      "fixed_income": null,
      "industry": null,
      "institution_id": null,
      "institution_security_id": null,
      "is_cash_equivalent": false,
      "isin": null,
      "iso_currency_code": "USD",
      "market_identifier_code": null,
      "name": "Nflx Feb 01'18 $355 Call",
      "option_contract": null,
      "proxy_security_id": null,
      "sector": null,
      "security_id": "8E4L9XLl6MudjEpwPAAgivmdZRdBPJuvMPlPb",
      "sedol": null,
      "subtype": "option",
      "ticker_symbol": "NFLX180201C00355000",
      "type": "derivative",
      "unofficial_currency_code": null,
      "update_datetime": null
    },
    {
      "cfi_code": "CIOIBS",
      "close_price": 9.08,
      "close_price_as_of": "2024-09-09",
      "cusip": null,
      "figi": null,
      "fixed_income": null,
      "industry": "Investment Trusts or Mutual Funds",
      "institution_id": null,
      "institution_security_id": null,
      "is_cash_equivalent": false,
      "isin": null,
      "iso_currency_code": "USD",
      "market_identifier_code": null,
      "name": "DoubleLine Total Return Bond I",
      "option_contract": null,
      "proxy_security_id": null,
      "sector": "Miscellaneous",
      "security_id": "AE5rBXra1AuZLE34rkvvIyG8918m3wtRzElnJ",
      "sedol": "B5ND9B4",
      "subtype": "mutual fund",
      "ticker_symbol": "DBLTX",
      "type": "mutual fund",
      "unofficial_currency_code": null,
      "update_datetime": null
    },
    {
      "cfi_code": null,
      "close_price": 42.15,
      "close_price_as_of": null,
      "cusip": null,
      "figi": null,
      "fixed_income": null,
      "industry": null,
      "institution_id": null,
      "institution_security_id": null,
      "is_cash_equivalent": false,
      "isin": null,
      "iso_currency_code": "USD",
      "market_identifier_code": null,
      "name": "iShares Inc MSCI Brazil",
      "option_contract": null,
      "proxy_security_id": null,
      "sector": null,
      "security_id": "abJamDazkgfvBkVGgnnLUWXoxnomp5up8llg4",
      "sedol": null,
      "subtype": "etf",
      "ticker_symbol": "EWZ",
      "type": "etf",
      "unofficial_currency_code": null,
      "update_datetime": null
    },
    {
      "cfi_code": null,
      "close_price": 1,
      "close_price_as_of": null,
      "cusip": null,
      "figi": null,
      "fixed_income": null,
      "industry": null,
      "institution_id": null,
      "institution_security_id": null,
      "is_cash_equivalent": true,
      "isin": null,
      "iso_currency_code": "USD",
      "market_identifier_code": null,
      "name": "U S Dollar",
      "option_contract": null,
      "proxy_security_id": null,
      "sector": null,
      "security_id": "d6ePmbPxgWCWmMVv66q9iPV94n91vMtov5Are",
      "sedol": null,
      "subtype": "cash",
      "ticker_symbol": null,
      "type": "cash",
      "unofficial_currency_code": null,
      "update_datetime": null
    }
  ]
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-profile",
        level: 4,
        title: "Get Plaid API keys and complete application profile",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "create-an-item-in-link",
        level: 4,
        title: "Create an Item in Link",
      },
{
        id: "get-a-persistent-access_token",
        level: 4,
        title: "Get a persistent access_token",
      },
{
        id: "fetching-investments-move-data",
        level: 4,
        title: "Fetching Investments Move data",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
